import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { FiPause, FiPlay } from 'react-icons/fi';
import { useShallow } from 'zustand/react/shallow';
import { useWeatherStore } from '../../store/weatherStore';
import { setPlaybackPosition } from '../../services/playbackClock';
import { useTranslation } from '../../i18n/LanguageContext';
import { getReadinessSnapshot, subscribeReadiness, FRAME_READY } from '../../services/rasterReadiness';

interface TimeSliderProps {
    times: Array<string | number>;
    currentTimeIndex: number;
    isPlaying: boolean;
    /** الرياح: خطوة سحب ساعية بدل 10 دقائق (بيانات المصدر ساعية فعلياً)، مع تمييز بصري. */
    hourlyMode?: boolean;
    /**
     * محور **يومي** (طبقة شذوذ سطح البحر): المقطع الواحد يوم كامل لا ساعة.
     *
     * ليس تمييزاً بصرياً بل تصحيح حساب: هذا المكوّن يفترض في ثلاثة مواضع أن المقطع
     * ساعةٌ واحدة — زمن المؤشّر (`+ fraction * 3600`)، وتجميع الأيّام، وعلامات الساعات.
     * على محور من ٦٠ يوماً كان الأوّل يُجمّد التاريخ المعروض (يتقدّم ساعةً داخل يوم
     * كامل)، والثاني يطبع اسم يوم لكل إطار فتتراكب ستّون تسمية فوق بعضها.
     */
    dailyMode?: boolean;
    /**
     * أوّل إطار **تنبّؤ** على محور الرادار (ما قبله رصد). يُظلَّل ما بعده ويُعلَّم
     * الحدّ خطّاً، وتقول الفقاعة «توقّع» — فلا يُقرأ المطر المتوقّع رصداً.
     */
    forecastFrom?: number;
}

function clamp(value: number, min: number, max: number) {
    return Math.min(max, Math.max(min, value));
}

function isSameDay(a: Date, b: Date) {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

// عرض كل إطار بالبكسل على المسطرة — ثابت بصرف النظر عن عدد الأيام (5 أو 10)، فالمسطرة
// تطول وتُسحب بدل أن تتقلّص علاماتها.
const PX_PER_FRAME = 22;

export function TimeSlider({ times, currentTimeIndex, isPlaying, hourlyMode = false, dailyMode = false, forecastFrom }: TimeSliderProps) {
    const { t, lang } = useTranslation();
    /** اسم الشهر بلغة الواجهة — مفاتيح month.1..12 (لا فهرس مصفوفة). */
    const monthName = (monthIndex: number) => t(`month.${monthIndex + 1}`);
    const {
        setTimelinePosition,
        setIsPlaying,
        frameFraction,
        setTimelineAutoSync,
        setTimelineSeeking,
        commitTimelineSeek,
    } = useWeatherStore(useShallow((s) => ({
        setTimelinePosition: s.setTimelinePosition,
        setIsPlaying: s.setIsPlaying,
        frameFraction: s.frameFraction,
        setTimelineAutoSync: s.setTimelineAutoSync,
        setTimelineSeeking: s.setTimelineSeeking,
        commitTimelineSeek: s.commitTimelineSeek,
    })));
    const trackRef = useRef<HTMLDivElement>(null);
    const dragState = useRef<{ startX: number; startContinuous: number } | null>(null);
    const pendingPosition = useRef<{ index: number; fraction: number } | null>(null);
    const dragRaf = useRef<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    // بعد أول سحب، يستقرّ نص الوقت داخل الشريط (بدل علامات الأيام) حتى بداية سحب جديد.
    const [isSettled, setIsSettled] = useState(false);
    /*
     * أصل المسطرة داخل المسار (بالبكسل من حافّته اليسرى) = منتصف الشريط **كلّه**، لا منتصف
     * المسار. زرّ التشغيل يسار المسار يُزيح منتصفه يميناً بنصف عرضه، فكان المؤشّر وفقاعة
     * الوقت خارج منتصف الشريط (طلب المالك 2026-09-13) — بلا فراغٍ مقابل يُهدر عرضاً.
     * يُقاس من المستطيلين الفعليين لا من ثابت، فيصحّ مع اختلاف حجم الزرّ بين المقاسات.
     * ويلزم أيضاً لتثبيت اسم اليوم عند الحافّة اليسرى أثناء السحب (كـWindy).
     */
    const [originX, setOriginX] = useState(0);
    useEffect(() => {
        const el = trackRef.current;
        const card = el?.parentElement;
        if (!el || !card) return;
        const measure = () => {
            const t = el.getBoundingClientRect();
            const c = card.getBoundingClientRect();
            setOriginX(c.left + c.width / 2 - t.left);
        };
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        ro.observe(card);
        measure();
        return () => ro.disconnect();
    }, []);

    useEffect(() => () => {
        if (dragRaf.current !== null) window.cancelAnimationFrame(dragRaf.current);
    }, []);

    const currentContinuous = currentTimeIndex + frameFraction;

    // بدء السحب: نلتقط موضع الإصبع/المؤشّر والموضع الزمني الحالي كمرجع، ثم نحرّك الزمن
    // بمقدار إزاحة السحب (مثل سحب مسطرة فعلية تحت مؤشّر ثابت في المنتصف).
    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        setTimelineAutoSync(false);
        /*
         * لمسُ الشريط يوقف التشغيل — نيّة المستخدم تسبق الحركة الآلية.
         *
         * كان التشغيل يستمرّ أثناء السحب، فيتنازع مصدران على الموضع نفسه: `useAutoplay`
         * يدفعه للأمام كل نبضة، والإصبع يجرّه إلى حيث يريد. النتيجة أن المؤشّر يقاوم
         * اليد ويقفز عائداً فور رفعها.
         *
         * ولا نستأنف بعد الرفع عمداً: من يسحب الشريط يريد فحص لحظة بعينها، واستئنافٌ
         * تلقائي يسحبها من تحته بعد جزء من الثانية. زرّ التشغيل باقٍ لمن أراد المتابعة.
         */
        setIsPlaying(false);
        setTimelineSeeking(true);
        event.currentTarget.setPointerCapture(event.pointerId);
        dragState.current = { startX: event.clientX, startContinuous: currentContinuous };
        setIsDragging(true);
        setIsSettled(false);
    };

    const flushDragPosition = () => {
        dragRaf.current = null;
        const position = pendingPosition.current;
        pendingPosition.current = null;
        if (position) setTimelinePosition(position.index, position.fraction);
    };

    const applyDrag = (clientX: number) => {
        if (!dragState.current) return;
        const deltaX = clientX - dragState.current.startX;
        // السحب لليمين يُرجع الزمن للخلف، وسحب لليسار يقدّمه للأمام (مثل شريط زمن حقيقي).
        const exact = clamp(
            dragState.current.startContinuous - deltaX / PX_PER_FRAME,
            0,
            Math.max(0, times.length - 1),
        );
        /*
         * الحقل البصري يتبع الإصبع **بدقّة كاملة**، والتسمية وحدها هي التي تُقرَّب.
         *
         * التقريب أدناه (10 دقائق) قرارٌ صحيح للتسمية: بيانات المصدر ساعية، وعرض ":07"
         * يوهم بدقّة غير موجودة. لكنه كان يحكم **الصورة** أيضاً، فيتقدّم الحقل اللوني
         * ستّ قفزات لكل ساعة سحب بدل أن ينساب تحت الإصبع. نفصل الأمرين: ساعة التشغيل
         * (‏وهي ما يقرؤه الـ GPU) تأخذ القيمة العشرية الخام، والمتجر يأخذ المقرَّبة.
         *
         * لا يمرّ هذا عبر React إطلاقاً، فلا كلفة إعادة رسم مهما بلغ معدّل pointermove.
         */
        const exactIdx = clamp(Math.floor(exact), 0, times.length - 1);
        setPlaybackPosition(exactIdx, exactIdx >= times.length - 1 ? 0 : exact - exactIdx);

        // خطوة 10 دقائق افتراضياً (Zoom Earth)، أو ساعة كاملة لطبقة الرياح — بيانات مصدرها
        // ساعية فعلياً، فخطوة أدقّ توهم بدقّة زمنية غير موجودة.
        const snapped = hourlyMode ? Math.round(exact) : Math.round(exact * 6) / 6;
        const idx = clamp(Math.floor(snapped), 0, times.length - 1);
        const fraction = idx >= times.length - 1 ? 0 : snapped - idx;
        // نتجاهل الحدث إن لم تتغيّر القيمة المقرَّبة فعلياً — pointermove يطلق عشرات
        // الأحداث لكل بكسل سحب، ودون هذا الحرس كانت كل واحدة تُطلق re-render لكامل شجرة
        // الخريطة (المتجر بلا selector) حتى حين تُسقَط على نفس خطوة الـ10 دقائق.
        const state = useWeatherStore.getState();
        const displayed = pendingPosition.current ?? { index: state.currentTimeIndex, fraction: state.frameFraction };
        if (displayed.index === idx && Math.abs(displayed.fraction - fraction) < 1e-6) return;
        pendingPosition.current = { index: idx, fraction };
        if (dragRaf.current === null) dragRaf.current = window.requestAnimationFrame(flushDragPosition);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.buttons & 1) applyDrag(event.clientX);
    };
    const handlePointerUp = () => {
        if (!dragState.current) return;
        dragState.current = null;
        if (dragRaf.current !== null) {
            window.cancelAnimationFrame(dragRaf.current);
            flushDragPosition();
        }
        setTimelineSeeking(false);
        commitTimelineSeek();
        setIsDragging(false);
        setIsSettled(true);
    };

    // الزمن المعروض = زمن الإطار + كسر الساعة (فيظهر :10، :20… مثل Zoom Earth).
    const baseEpoch = times[currentTimeIndex] ? Number(times[currentTimeIndex]) : Date.now() / 1000;
    // طول المقطع بالثواني: يوم على المحور اليومي، وساعة على غيره. كان 3600 مثبَّتاً.
    const frameSeconds = dailyMode ? 86400 : 3600;
    const currentTime = new Date((baseEpoch + frameFraction * frameSeconds) * 1000);
    const now = new Date();

    // فقاعة الزمن فوق المؤشّر (كفقاعة Windy الصفراء): **الساعة وحدها** — اليوم صار مكتوباً
    // داخل الشريط نفسه تحت المؤشّر، فتكراره في الفقاعة يطيلها بلا معلومة جديدة.
    const cursorLabel = useMemo(() => {
        const tomorrow = new Date(now);
        tomorrow.setDate(now.getDate() + 1);
        const dayLabel = isSameDay(currentTime, now)
            ? t('time.today')
            : isSameDay(currentTime, tomorrow)
                ? t('time.tomorrow')
                : `${currentTime.getDate()} ${monthName(currentTime.getMonth())}`;
        // على المحور اليومي التسمية **تاريخ بلا ساعة**: الرصد متوسّط يوم كامل، فطباعة
        // «2:00 مساءً» عليه تدّعي دقّة زمنية لا وجود لها في البيانات.
        if (dailyMode) return dayLabel;
        const hours = currentTime.getHours();
        const minutes = currentTime.getMinutes();
        const period = t(hours < 12 ? 'time.am' : 'time.pm');
        const h12 = hours % 12 === 0 ? 12 : hours % 12;
        const timeStr = `${h12}:${String(minutes).padStart(2, '0')}`;
        return `${timeStr} ${period}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentTime.getTime(), lang, dailyMode]);

    // موضع كل إطار بالبكسل نسبةً إلى المؤشّر الثابت في المنتصف: يتحرّك الشريط (لا المؤشّر)
    // أثناء السحب، تماماً كمسطرة قابلة للف تحت علامة زمن ثابتة.
    const framePx = (idx: number) => (idx - currentContinuous) * PX_PER_FRAME;

    // علامات الأيام: تجميع كل يوم على حدة لعرض اسمه في منتصف قطعته على المسطرة.
    const dayMarkers = useMemo(() => {
        const groups: { label: string; startIdx: number; endIdx: number }[] = [];
        // المحور اليومي: تسمية لكل **شهر** لا لكل إطار. التجميع أدناه يفتح مجموعة عند
        // كل يوم جديد، وهنا كل إطار يوم جديد — أي ستّون تسمية على مسطرة عرض مقطعها
        // 22 بكسل، تتراكب حتى تصير خطّاً غير مقروء.
        if (dailyMode) {
            times.forEach((t, idx) => {
                const d = new Date(Number(t) * 1000);
                const last = groups[groups.length - 1];
                if (last && new Date(Number(times[last.startIdx]) * 1000).getMonth() === d.getMonth()) {
                    last.endIdx = idx;
                } else {
                    groups.push({ label: monthName(d.getMonth()), startIdx: idx, endIdx: idx });
                }
            });
            return groups;
        }
        // ‏`tm` لا `t`: الاسم `t` يحجب دالّة الترجمة المستعملة في التسمية أدناه.
        times.forEach((tm, idx) => {
            const d = new Date(Number(tm) * 1000);
            const last = groups[groups.length - 1];
            if (last && isSameDay(new Date(Number(times[last.startIdx]) * 1000), d)) {
                last.endIdx = idx;
            } else {
                // «الاثنين 13» كـWindy: اسم اليوم أسرع قراءةً من الشهر عند التخطيط لأيام قادمة.
                groups.push({
                    label: `${t(`day.${d.getDay()}`)} ${d.getDate()}`,
                    startIdx: idx,
                    endIdx: idx,
                });
            }
        });
        return groups;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [times, dailyMode, lang]);

    /*
     * ── شريط الجاهزية ────────────────────────────────────────────────────────────
     *
     * الإحماء نافذة متحرّكة (‏±8 ساعات خارج التشغيل، +16 أثناءه)، لا الشريط كلّه. فقفزة
     * إلى ساعة بعيدة تنتظر تنزيلاً وفكّاً — والمستخدم لا يملك ما يفسّر له ذلك، فيبدو
     * التوقّف عشوائياً. نرسم تحت المسطرة قطعةً لكل ساعة: مضيئة إن كانت كلّ مدخلات إطارها
     * مفكوكة وجاهزة، ورماديّة باهتة إن لم تكن.
     *
     * قراءة فقط — لا يُطلق هذا شيئاً ولا يزاحم الطابور؛ يعرض ما تعرفه ذاكرة النُسج.
     */
    const readiness = useSyncExternalStore(subscribeReadiness, getReadinessSnapshot);

    // ندمج الساعات المتجاورة المتماثلة في مقاطع: 121 عنصر DOM لكل إطار سحب كلفةٌ بلا
    // مقابل، بينما المقاطع الفعلية حفنة (نافذة محمّاة واحدة أو اثنتان).
    const readySpans = useMemo(() => {
        const spans: { start: number; end: number; ready: boolean }[] = [];
        const n = Math.min(readiness.length, times.length);
        for (let i = 0; i < n; i++) {
            const ready = readiness[i] === FRAME_READY;
            const last = spans[spans.length - 1];
            if (last && last.ready === ready && last.end === i - 1) last.end = i;
            else spans.push({ start: i, end: i, ready });
        }
        return spans;
    }, [readiness, times.length]);

    // علامات الساعات: خطوط صغيرة على طول المسطرة، أطول عند رأس كل يوم/كل 6 ساعات.
    const hourTicks = useMemo(() => {
        return times.map((t, idx) => {
            const d = new Date(Number(t) * 1000);
            if (dailyMode) {
                // العلامة الطويلة عند أوّل الشهر، والمتوسّطة كل أحد — إيقاع أسبوعي يقرأه
                // العين على مسطرة شهرين. شرط `getHours()===0` أدناه لا يتحقّق هنا أصلاً:
                // أزمنة OISST مثبّتة على 12:00 UTC، فكانت المسطرة تخرج بلا أي علامة يوم.
                return { idx, isDayStart: d.getDate() === 1, isMajor: d.getDay() === 0 };
            }
            const isDayStart = d.getHours() === 0;
            const isMajor = d.getHours() % 6 === 0;
            // رقم الساعة (06 / 12 / 18) كأرقام Windy — عند **رأس الساعة فقط**: محورا الرادار
            // والقمر إطاراتهما دقائق، فبدون شرط الدقائق تُطبع «12» على كل إطارات تلك الساعة.
            // والـ00 لا تُطبع: موضعها بداية اليوم، واسم اليوم يحلّ محلّها.
            const hourLabel = isMajor && !isDayStart && d.getMinutes() === 0
                ? String(d.getHours()).padStart(2, '0') : null;
            return { idx, isDayStart, isMajor, hourLabel };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [times, lang, dailyMode]);

    return (
        <div className={`time-slider-root absolute bottom-[14px] left-1/2 -translate-x-1/2 w-[350px] max-w-[calc(100%-20px)] ${isDragging ? 'z-[2060]' : 'z-[1100]'} flex pointer-events-none max-[768px]:bottom-[max(10px,env(safe-area-inset-bottom))] max-[768px]:w-[calc(100vw-12px)] max-[768px]:max-w-[calc(100vw-12px)] max-[420px]:bottom-[max(8px,env(safe-area-inset-bottom))]`}>
            {/* توقيع الموقع ملاصقاً للشريط (كـ windy.com): الشعار أعلى الشاشة يسقط من أي
                لقطة أو فيديو يُقصّ أعلاه، أمّا الشريط فيبقى في القصّ دائماً لأنه ما يُفهم به
                الوقت — فالتوقيع يرافقه. */}
            <span className="timeline-signature absolute -top-[15px] right-2 text-[10px] font-bold tracking-[0.3px] leading-none text-white/60 light:text-black/50 [text-shadow:0_1px_3px_rgba(0,0,0,0.6)] light:[text-shadow:none] pointer-events-none select-none [direction:ltr]">
                skyradarweather.com
            </span>
            {/* الشريط ltr في اللغتين عمداً: محور الزمن نفسه ltr وحسابات framePx مبنيّة عليه. */}
            <div className="w-full flex [direction:ltr] items-center gap-1.5 px-2 py-1.5 rounded-xl bg-[rgba(24,28,38,0.88)] light:bg-[var(--panel-bg)] backdrop-blur-[14px] border border-white/[0.08] light:border-[var(--panel-border)] light:text-[var(--text-primary)] shadow-[0_10px_30px_rgba(0,0,0,0.32)] light:shadow-[var(--panel-shadow)] pointer-events-auto max-[768px]:min-h-[58px] max-[768px]:px-2 max-[768px]:py-[7px] max-[768px]:gap-[7px] max-[768px]:rounded-[14px] max-[560px]:max-w-full">
                <button
                    className="flex-none w-[26px] h-[26px] flex items-center justify-center border-none rounded-lg bg-transparent text-white light:text-[var(--text-primary)] text-base cursor-pointer transition-[background] duration-150 ease hover:bg-white/10 light:hover:bg-black/10 max-[768px]:w-11 max-[768px]:h-11 max-[768px]:min-w-11 max-[768px]:text-[23px] max-[768px]:rounded-[11px]"
                    onClick={() => {
                        setTimelineAutoSync(false);
                        setIsPlaying(!isPlaying);
                    }}
                    title={t(isPlaying ? 'time.pause' : 'time.play')}
                    aria-label={t(isPlaying ? 'time.pause' : 'time.play')}
                    type="button"
                >
                    {isPlaying ? <FiPause /> : <FiPlay />}
                </button>
                <div
                    className="relative flex-1 h-[40px] cursor-grab active:cursor-grabbing select-none [direction:ltr] max-[768px]:h-12 max-[768px]:min-w-0"
                    ref={trackRef}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    onPointerCancel={handlePointerUp}
                    style={{ touchAction: 'none' }}
                >
                    {/* نافذة قصّ للمسطرة وحدها — تُبقي المؤشّر وتسميته العائمة غير مقصوصين. */}
                    <div className="absolute inset-0 overflow-hidden">
                        <div className="absolute inset-y-0 right-0" style={{ left: originX }}>
                            {/*
                              * كتل الأيام — تحليل Windy وZoom Earth معاً:
                              *   • كل يوم كتلة بخلفية متناوبة، فيُرى امتداد اليوم لا مجرّد خطّ فاصل.
                              *   • الاسم «الاثنين 13» مثبَّت عند حافّة الكتلة المرئية اليسرى: اليوم
                              *     الذي بدأ خارج الإطار يبقى اسمه ظاهراً، ويدفعه اليوم التالي خارجاً
                              *     حين يصل (قصّ الكتلة نفسها يصنع ذلك بلا حساب إضافي).
                              */}
                            {dayMarkers.map((g, gi) => {
                                const segL = framePx(g.startIdx - 0.5);
                                const segR = framePx(g.endIdx + 0.5);
                                // 14 = فوق عرض تدرّج الحافّة (18) تقريباً، كي لا يُبهت أوّل حرف.
                                const labelOffset = Math.max(0, -originX - segL) + 14;
                                return (
                                    <div
                                        key={`d-${g.startIdx}`}
                                        className={`absolute top-0 bottom-0 overflow-hidden ${gi % 2
                                            ? 'bg-white/[0.045] light:bg-black/[0.04]'
                                            : ''} ${gi > 0 ? 'border-l border-white/[0.16] light:border-black/[0.14]' : ''}`}
                                        style={{ left: `${segL}px`, width: `${segR - segL}px` }}
                                    >
                                        <span
                                            className="absolute top-[3px] whitespace-nowrap [unicode-bidi:plaintext] text-[11px] font-bold text-white/90 light:text-[var(--text-primary)] max-[768px]:text-xs"
                                            style={{ left: `${labelOffset}px` }}
                                        >
                                            {g.label}
                                        </span>
                                    </div>
                                );
                            })}

                            {/* مقطع التنبّؤ: تظليلٌ مخطّط وخطّ عند الحدّ (أسلوب Windy). */}
                            {forecastFrom !== undefined && forecastFrom < times.length && (
                                <div
                                    className="absolute top-0 bottom-0 border-l-2 border-[#facc15]/80 bg-[repeating-linear-gradient(135deg,rgba(250,204,21,0.10)_0_4px,transparent_4px_8px)]"
                                    style={{
                                        left: `${framePx(forecastFrom - 0.5)}px`,
                                        width: `${(times.length - forecastFrom + 0.5) * PX_PER_FRAME}px`,
                                    }}
                                />
                            )}

                            {/* يُرسم قبل العلامات كي تبقى العلامات فوقه مقروءة. */}
                            {readySpans.map((sp) => (
                                <div
                                    key={`r-${sp.start}`}
                                    className={`absolute bottom-0 h-[2px] rounded-sm ${sp.ready
                                        ? 'bg-[rgba(96,165,250,0.75)] light:bg-[rgba(37,99,235,0.55)]'
                                        : 'bg-white/[0.13] light:bg-black/[0.13]'}`}
                                    style={{
                                        left: `${framePx(sp.start - 0.5)}px`,
                                        width: `${(sp.end - sp.start + 1) * PX_PER_FRAME}px`,
                                    }}
                                    title={t(sp.ready ? 'time.frameReady' : 'time.frameLoading')}
                                />
                            ))}

                            {hourTicks.map((tk) => (
                                <div
                                    key={tk.idx}
                                    className={`absolute bottom-1.5 w-px max-[768px]:bottom-[9px] ${tk.isDayStart
                                        ? 'h-3 w-[1.5px] bg-white/60 light:bg-black/50 max-[768px]:h-4'
                                        : tk.isMajor
                                            ? 'h-2 bg-white/40 light:bg-black/[0.32] max-[768px]:h-[11px]'
                                            : 'h-[5px] bg-white/[0.28] light:bg-black/[0.22] max-[768px]:h-[7px]'}`}
                                    style={{ left: `${framePx(tk.idx)}px` }}
                                />
                            ))}

                            {hourTicks.map((tk) => tk.hourLabel && (
                                <span
                                    key={`h-${tk.idx}`}
                                    className="absolute bottom-[19px] -translate-x-1/2 text-[9px] leading-none tabular-nums text-white/50 light:text-black/45 max-[768px]:bottom-[26px] max-[768px]:text-[10px]"
                                    style={{ left: `${framePx(tk.idx)}px` }}
                                >
                                    {tk.hourLabel}
                                </span>
                            ))}
                        </div>

                        <div className="absolute top-0 bottom-0 w-[18px] pointer-events-none z-[4] left-0 bg-[linear-gradient(90deg,rgba(24,28,38,0.88),transparent)] light:bg-[linear-gradient(90deg,var(--panel-bg),transparent)]" />
                        <div className="absolute top-0 bottom-0 w-[18px] pointer-events-none z-[4] right-0 bg-[linear-gradient(270deg,rgba(24,28,38,0.88),transparent)] light:bg-[linear-gradient(270deg,var(--panel-bg),transparent)]" />
                    </div>

                    <div className="absolute top-0 bottom-0 -translate-x-1/2 flex flex-col items-center pointer-events-none z-[5]" style={{ left: originX }}>
                        {/*
                          * فقاعة الزمن **فوق** الشريط دائماً (فقاعة Windy الصفراء، وفقاعة Zoom Earth
                          * البيضاء): كانت داخله فتزاحم أسماء الأيام. وأثناء السحب ترتفع وتكبر —
                          * الإصبع فوق الشريط يغطّي ما تحته، والفقاعة المرتفعة تبقى مقروءة فوقه.
                          */}
                        {/* بيضاء (طلب المالك 2026-09-13، كفقاعة Zoom Earth). وحالتان فقط:
                            ساكنة صغيرة، ونشطة (سحب **أو تشغيل**) أعلى قليلاً وأكبر قليلاً —
                            تكبيرٌ معتدل لا قفزة: 1.18 السابقة بدت ضخمة فوق الشريط. */}
                        <div
                            className={`absolute left-1/2 whitespace-nowrap [unicode-bidi:plaintext] rounded-lg px-2 py-[2px] bg-white text-[#15171c] font-bold transition-[top,transform,box-shadow] duration-[0.18s] ease ${isDragging || isPlaying
                                ? 'top-[-38px] -translate-x-1/2 scale-[1.08] text-[12px] shadow-[0_6px_16px_rgba(0,0,0,0.4)] max-[768px]:top-[-42px]'
                                : 'top-[-30px] -translate-x-1/2 scale-100 text-[11px] shadow-[0_3px_10px_rgba(0,0,0,0.3)] max-[768px]:top-[-33px] max-[768px]:text-[12px]'}`}
                        >
                            {cursorLabel}
                            {forecastFrom !== undefined && currentTimeIndex >= forecastFrom
                                && <span className="ms-1 text-[#a16207]">· {t('time.nowcast')}</span>}
                            {/* السهم نحو المؤشّر — يربط الفقاعة بموضعها على المسطرة. */}
                            <span className="absolute left-1/2 -bottom-[3px] -translate-x-1/2 w-1.5 h-1.5 rotate-45 bg-white" />
                        </div>
                        {isDragging
                            ? <div className={`w-0.5 h-full rounded-sm ${hourlyMode ? 'bg-[rgba(94,200,255,0.55)]' : 'bg-[#3b82f6]'}`} />
                            : <div className={`w-[3px] h-1/2 rounded-sm mt-auto max-[768px]:h-[54%] ${hourlyMode ? 'bg-[rgba(94,200,255,0.55)]' : 'bg-[#3b82f6]'}`} />}
                        <div className={`absolute bottom-[-3px] w-1.5 h-1.5 rounded-full max-[768px]:bottom-[-2px] max-[768px]:w-2 max-[768px]:h-2 ${hourlyMode ? 'bg-[#5ec8ff] shadow-[0_0_0_4px_rgba(94,200,255,0.22)]' : 'bg-[#3b82f6]'}`} />
                    </div>
                </div>
                {/* لا زرّ «القفز لآخر إطار»: يرمي إلى نهاية التوقّع (بعد أيام) فلا ينفع في التصفّح
                    — طلب المالك 2026-09-13. والمسطرة تستعيد عرضه للأيام. */}
            </div>
        </div>
    );
}
